import { MetaResponse } from "@/common/types/metaResponse"
import { stateStrategy } from "../constants/stateStrategy.constant"
import { AddressData } from "@/common/types/address"
import { GeoGeoableData } from "@/common/types/geoGeoable"
import { UserData } from "@/common/types/users"
import { ProductCategoryData } from "@/features/productCategories/types/productCategories"
import { LogType } from "@/common/types/lead/log.type"
import { PrinterMachineData } from "@/common/types/printerMachine.type"
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { FeeTemplateData } from "@/features/feeTemplates/types/feeTemplates.type";

export type SpecialEntriesData = {
  id: string
  fee_template: FeeTemplateData
  temporary_target_id?: string
  temporary_target_name?: string
  temporary_target_type?: string
  target?: EstablishmentCompanyData
  value: number
  not_use_if_has_special_fee?: boolean
  execution_priority?: number
}

export type RequirementListSummaryData = {
  description: string
  steps: {
    name: string
    passed: boolean
  }[]
}

export type OperationalInformationData = {
  expected_equipment_quantity?: string
  expected_credit?: number
  geo_geoables?: GeoGeoableData[]
  product_categories?: ProductCategoryData[]
  last_review?: LastReview
  industry_segment: IndustrySegmentData
}

export type FiscalDocumentInformationData = {
  consumption_observations?: string
  service_observations?: string
  seller_cnpj?: string
  last_review?: LastReview
  fiscal_document_kind?: string
}

export type IndustrySegmentData = {
  id: string
  display_name: string
  perma_name: string
}

export type GeneralInformationData = {
  cnpj?: string
  email?: string
  fantasy_name?: string
  official_name?: string
  primary_cnae?: string
  score?: string
  state_inscription?: string
  company_logo?: {
    url: string
    content_type: string
  }
  last_review?: LastReview
  address: AddressData;
}

export type FinancialInformationData = {
  approved_credit?: number
  last_review: LastReview
}

export type ContractEquipmentCategoryData = {
  id: string
  equipment_category: {
    id: string
    description: string
    display_name: string
  }
  quantity: number
}

export type ContractFeeData = {
  id: string
  fee_template: {
    id: string
    applicable_entity: string
    calculate_as: 'fixed' | 'percentage'
    display_name: string
    updated_at: string
  }
  value: number
  main_fee: boolean
}

export type Cycle = 'weekly' | 'fortnightly' | 'monthly' | 'fixed'

export type SignatureType = 'manual' | 'clicksign'

export type ContractData = {
  id: string
  bill_grouping_mode?: string
  bill_by?: string
  consumption_controlled_by?: 'currency' | 'quantity'
  cycle?: Cycle
  cycle_start_day?: number
  cycle_fixed_interval?: number
  closing_to_due_in_days?: number
  cnpj_for_fiscal_document?: string
  contract_equipment_categories?: ContractEquipmentCategoryData[]
  contract_fees?: ContractFeeData[]
  contract_product_categories?: {
    id: string
    product_category: ProductCategoryData
  }[]
  granted_credit?: number
  due_to_suspension_in_days?: number
  duration_in_months?: number
  file: {
    content_type?: string
    url?: string
    created_at?: string
  }
  file_attached_at?: string
  fiscal_document_requirement?: string
  geo_geoables?: GeoGeoableData[]
  in_effect_since?: string
  in_effect_until?: string
  last_review?: LastReview
  payment_method?: string
  payment_modality?: string
  protocol_number?: string
  state: string
  default_fuel_price_rule: string
  signature_type: SignatureType
}

export type LastReview = {
  description?: string
  state: 'disapproved' | 'approved'
}

export enum ContactState {
  pending = 'pending',
  awaiting_client_documents = 'awaiting_client_documents',
  in_review = 'in_review',
  complete = 'complete'
}

export type ContactData = {
  id: string
  email?: string
  full_name?: string
  last_review?: LastReview
  role_in_company?: string
  cpf?: string
  phone?: {
    number: string
    kind: string
    whatsapp_capability: string
  }
  responsible_for_company_information?: boolean
  documents?: {
    id?: string
    file?: {
      id: string
      url: string
      content_type: string
    }
    issued_on_date?: string
    expires_at?: string
    rg?: string
    type?: string
    last_review?: LastReview
    uploaded_by?: UserData
    cnh?: string
  }[]
  requirement_list_summary?: RequirementListSummaryData
  state: ContactState
}

export type LastStateChangeLog = {
  changed_by: UserData
  changed_by_type: string
  created_at: string
  loggable_id: string
  loggable_type: string
  message: string
  next_state: keyof typeof stateStrategy
  previous_state: keyof typeof stateStrategy
  source_id: string
  source_type: string
}

export type PrivateCompanyLeadData = {
  id: string
  display_name: string
  observations: string
  state: keyof typeof stateStrategy
  creator: UserData
  commercial_manager: UserData
  commercial_manager_substitute: UserData | null
  contact_for_master_user?: ContactData
  operational_information?: OperationalInformationData
  fiscal_document_information?: FiscalDocumentInformationData
  general_information?: GeneralInformationData
  generic_documents: {
    id: string
    attachment_id: string
    content_type: string
    url: string
  }[]
  financial_information?: FinancialInformationData
  address?: AddressData & { last_review: LastReview }
  contacts?: ContactData[]
  contract?: ContractData
  requirement_list_summary?: RequirementListSummaryData
  last_review: LastReview
  logs?: LogType[]
  created_at: string
  archived_at?: string
  last_state_change_log?: LastStateChangeLog
  printer_machine?: PrinterMachineData
  fee_rule?: {
    special_entries: SpecialEntriesData[];
    last_review: LastReview;
  };
}

export type PrivateCompanyLeadsResponse = {
  data: {
    data: PrivateCompanyLeadData[]
    meta: MetaResponse
  }
}

export type PrivateCompanyLeadResponse = {
  data: {
    data: PrivateCompanyLeadData
  }
}

export type PrivateCompanyLeadContactResponse = {
  data: {
    data: ContactData
  }
}

export type PrivateCompanyLeadTransformerRespose = {
  registered?: PrivateCompanyLeadData[]
  qualifying?: PrivateCompanyLeadData[]
  quoting?: PrivateCompanyLeadData[]
  negotiating?: PrivateCompanyLeadData[]
  completed?: PrivateCompanyLeadData[]
}

export type LeadFormStateProps = {
  id: string;
  privateCompanyLead: PrivateCompanyLeadData;
}
